import { create } from 'zustand'

import type { PageSnapshot } from '@scrambl/shared'
import { parsePageSnapshot } from '@scrambl/shared'

export interface PageSnapshotScope {
  /** Includes the authenticated request generation, workspace and route. */
  key: string
  isCurrent: () => boolean
  route: PageSnapshot['route']
}

export type PageSnapshotContent = Pick<
  PageSnapshot,
  'module' | 'record' | 'selection' | 'filters' | 'view'
> & { surfaceKey?: string; route?: PageSnapshot['route'] }
interface Publisher {
  scope: PageSnapshotScope
  content: PageSnapshotContent
  order: number
  kind: 'surface' | 'filters'
}

interface PageSnapshotState {
  activeScope: PageSnapshotScope | null
  publishers: Record<string, Publisher>
  snapshots: Record<string, PageSnapshot | undefined>
  dismissed: Record<string, boolean>
  sequence: number
  publish: (
    scope: PageSnapshotScope,
    source: string,
    content: PageSnapshotContent,
    kind?: 'surface' | 'filters',
  ) => void
  remove: (scopeKey: string, source: string) => void
  focus: (scopeKey: string, source: string) => void
  dismiss: (scopeKey: string) => void
  restore: (scopeKey: string) => void
  retainScope: (scope: PageSnapshotScope | null) => void
}

function resolve(
  publishers: Record<string, Publisher>,
  scope: PageSnapshotScope,
): PageSnapshot {
  const candidates = Object.values(publishers)
    .filter(item => item.scope.key === scope.key)
    .sort((a, b) => b.order - a.order)
  const active =
    candidates.find(item => item.kind === 'surface') ?? candidates[0]
  const filterPublisher = !active?.content.record
    ? candidates.find(
        item =>
          item.kind === 'filters' &&
          item.content.surfaceKey &&
          item.content.surfaceKey === active?.content.surfaceKey,
      )
    : undefined

  return {
    version: 1,
    route: scope.route,
    ...(active?.content ?? {}),
    ...(filterPublisher
      ? {
          filters: {
            ...active?.content.filters,
            ...filterPublisher.content.filters,
          } as PageSnapshot['filters'],
        }
      : {}),
    capturedAt: new Date().toISOString(),
  }
}

/** Ephemeral, owner-scoped UI state. Background refreshes never steal focus. */
export const usePageSnapshotStore = create<PageSnapshotState>(set => ({
  activeScope: null,
  publishers: {},
  snapshots: {},
  dismissed: {},
  sequence: 0,
  publish: (scope, source, content, kind = 'surface') =>
    set(state => {
      const previous = state.publishers[source]
      const order =
        previous?.scope.key === scope.key ? previous.order : state.sequence + 1
      const publishers = {
        ...state.publishers,
        [source]: { scope, content, order, kind },
      }
      return {
        publishers,
        sequence: Math.max(state.sequence, order),
        snapshots: {
          ...state.snapshots,
          [scope.key]: resolve(publishers, scope),
        },
      }
    }),
  remove: (scopeKey, source) =>
    set(state => {
      const previous = state.publishers[source]
      // A late cleanup from the previous route cannot remove its replacement.
      if (previous?.scope.key !== scopeKey) return state
      const publishers = { ...state.publishers }
      delete publishers[source]
      return {
        publishers,
        snapshots: {
          ...state.snapshots,
          [scopeKey]: resolve(publishers, previous.scope),
        },
      }
    }),
  focus: (scopeKey, source) =>
    set(state => {
      const previous = state.publishers[source]
      if (previous?.scope.key !== scopeKey) return state
      const sequence = state.sequence + 1
      const publishers = {
        ...state.publishers,
        [source]: { ...previous, order: sequence },
      }
      return {
        sequence,
        publishers,
        snapshots: {
          ...state.snapshots,
          [scopeKey]: resolve(publishers, previous.scope),
        },
      }
    }),
  dismiss: scopeKey =>
    set(state => ({ dismissed: { ...state.dismissed, [scopeKey]: true } })),
  restore: scopeKey =>
    set(state => ({ dismissed: { ...state.dismissed, [scopeKey]: false } })),
  retainScope: scope =>
    set(state => {
      const scopeKey = scope?.key ?? null
      return {
        activeScope: scope,
        publishers: Object.fromEntries(
          Object.entries(state.publishers).filter(
            ([, value]) => value.scope.key === scopeKey,
          ),
        ),
        snapshots:
          scopeKey && scope
            ? {
                [scopeKey]:
                  state.snapshots[scopeKey] ?? resolve(state.publishers, scope),
              }
            : {},
        dismissed: scopeKey
          ? { [scopeKey]: state.dismissed[scopeKey] ?? false }
          : {},
      }
    }),
}))

/** Read at submission, not from a closure captured before selection changed. */
export function capturePageSnapshot(
  scope: PageSnapshotScope | null,
): PageSnapshot | undefined {
  if (!scope || !scope.isCurrent()) return undefined
  const state = usePageSnapshotStore.getState()
  if (state.dismissed[scope.key]) return undefined
  return parsePageSnapshot({
    ...(state.snapshots[scope.key] ?? {
      version: 1,
      route: scope.route,
    }),
    capturedAt: new Date().toISOString(),
  })
}

export function captureActivePageSnapshot() {
  return capturePageSnapshot(usePageSnapshotStore.getState().activeScope)
}
